/**
 * Snapshot v2 — narrative-first контракт страницы проекта.
 *
 * Что и зачем
 * ───────────
 * v1 (`./snapshot.ts`) построен вокруг 48 нарративов в 4 секциях с
 * унифицированным `SnapshotAspect.kind = 'glossary' | 'cross-cutting'`.
 * Web рендерил его как «дашборд + аккордеон секций».
 *
 * v2 переворачивает приоритет: главная поверхность — **Canvas-view**
 * (страница проекта = «О проекте» + по странице на каждый Канвас), а
 * нарративы делятся на два не пересекающихся рода с разными shape'ами:
 *
 * 1. NarrativeAspect — слот в Canvas-view (project-wide или per-Канвас).
 *    Имеет LLM-summary с staleness-tracking'ом + структурный `blocks`
 *    с цитатами Сигналов/Развилок/Премисс, прикреплёнными к узлам.
 *
 * 2. HealthAspect — диагностика модели (пустоты, противоречия,
 *    протухания). Без summary, без scope, без позиции на странице —
 *    показывается отдельно в top-bar бэйдже и Problems-панели; флаги
 *    касающиеся конкретных узлов также инлайнятся в NarrativeBlock.health.
 *
 * Справочник (типологический индекс) — отдельная производная структура
 * `Glossary` для боковой панели «Сущности», без нарративов.
 *
 * `Severity`, `AspectMetric`, `TreeNodeLocation`, `SnapshotMeta`,
 * `SnapshotDiagnostics` — переиспользуются из v1: атомы, не привязанные
 * к старому aspect-контракту. Когда v1 будет снесён, эти типы либо
 * переедут сюда, либо останутся в отдельном `atoms.ts`.
 *
 * Связанные документы:
 * - Дизайн обсуждения и принципы — переписка по эпику «brown-gTE5J».
 * - Маппинг 21 продуктового вопроса на нарративы — см.
 *   `templates/_shared/docs/dsl-guide.md` и продуктовые комментарии к `NarrativeAspectId`.
 */

import type {
    AspectMetric,
    Severity,
    SnapshotDiagnostics,
    SnapshotMeta,
    TreeNodeLocation,
} from './snapshot';

// Реэкспорт атомов, которые переиспользуются из v1, чтобы потребители
// `@pflow/shared/v2` имели один import-источник и не лазили в v1.
export type {
    AspectMetric,
    Severity,
    SnapshotDiagnostics,
    SnapshotMeta,
    TreeNodeLocation,
};

// ─────────────────────────────────────────────────────────────────────
// Идентификаторы нарративов
// ─────────────────────────────────────────────────────────────────────

/**
 * Project-level narrative нарративы. Структура следует «Концепту проекта
 * агентства»: project-уровень — обвязка концепта (что за проект, рынок,
 * рамки, развилки, evidence, валидация), вся BMC-фактура живёт на
 * canvas-уровне (аспект `model`).
 *
 * Фрейм (показывается до Канвасов):
 *   intro           — что за проект: Юнит(ы), статус, ключевая ставка,
 *                     логика портфеля (Связки/Петли между Канвасами).
 *   market          — что делает рынок: рыночные Юниты-конкуренты
 *                     (Принадлежность: рынок), мосты Альтернатива.Модель,
 *                     сигналы по конкурентам.
 *   frames          — Премиссы (scope-ограничения, founder's bets, пивоты).
 *   decisions       — Развилки (точки стратегического выбора с историей).
 *
 * Бэкстейдж (показывается после Канвасов):
 *   evidence        — Сигналы по нашим сущностям (доказательная база).
 *   validation      — что проверяем и куда двигаем: Результаты/Показатели
 *                     (метрики-ставки) + dflow План/Итерации/Гипотезы +
 *                     Соответствия (Four Fits) + Кольца каналов.
 *   open-questions  — синтетический «что проверять дальше»: открытые
 *                     Развилки + Ситуация.Неясно + Премиссы.на_срок +
 *                     Соответствия «под_угрозой». Чек-лист, идёт в конце.
 */
export type ProjectNarrativeAspectId =
    | 'narr.project.intro'
    | 'narr.project.market'
    | 'narr.project.frames'
    | 'narr.project.decisions'
    | 'narr.project.evidence'
    | 'narr.project.validation'
    | 'narr.project.open-questions';

/**
 * Canvas-level narrative нарративы. Порядок и состав следуют «Концепту
 * проекта агентства»: канвас → профиль клиента → лестница → упаковка →
 * демо-кейс → блупринт → риски → триггеры → фин модель.
 *
 *   model       — «Канвас агентства»: 9 блоков BMC одной страницей
 *                 (сегменты кратко, ценность/УТП, каналы + Кольцо,
 *                 отношения, доходы, ресурсы, активности, партнёры,
 *                 затраты, компоненты) + логика связности модели.
 *   audience    — профиль клиента: Сегменты (+Не_наш, ЛПР, Ранние) +
 *                 Работы JTBD + Боли/Выгоды профиля VPC.
 *   ladder      — продуктовая лестница: VPC-карты ценности по ступеням
 *                 (Тип_выгоды, Снимает/Создаёт), связность лестницы,
 *                 Альтернативы, ERRC.
 *   product     — упаковка: svcflow:Продукт + Уровни/Опции + Трек +
 *                 Апгрейды + границы Не_входит.
 *   case        — демо-кейс: svcflow:Кейсы (клиент через лестницу,
 *                 этапы с результатами). Существует если Кейсы есть.
 *   blueprint   — Service Blueprint: Блупринты Услуг Канваса (Шаги ×
 *                 слои, Видимость_критична). Существует если есть.
 *   risks       — карта рисков: Риски (FMEA, RPN), Моменты истины,
 *                 красные зоны, контрмеры. Существует если есть.
 *   triggers    — триггеры и вход: Ситуации (CEP) + Переключения
 *                 (4 силы) + из чего складывается сообщение.
 *   economics   — фин модель: Доходы/Расходы + unit-economics каналов
 *                 (CAC/LTV/payback) + P&L.
 */
export type CanvasNarrativeAspectId =
    | 'narr.canvas.model'
    | 'narr.canvas.audience'
    | 'narr.canvas.ladder'
    | 'narr.canvas.product'
    | 'narr.canvas.case'
    | 'narr.canvas.blueprint'
    | 'narr.canvas.risks'
    | 'narr.canvas.triggers'
    | 'narr.canvas.economics';

export type NarrativeAspectId =
    | ProjectNarrativeAspectId
    | CanvasNarrativeAspectId;

/**
 * Health-нарративы. Каждый — отдельный детектор; список плоский, без
 * иерархии секций.
 *
 *   unfounded         — узлы без Сигнала и без твёрдой Премиссы.
 *   disputed          — противоречия в модели (Сценарий с Силой:низкая
 *                       под действующей Услугой и т.п.).
 *   stale-signals     — Сигналы старше порога давности.
 *   empty-fields      — пустые Описание/УТП/Преимущество, без которых
 *                       Canvas-view деградирует.
 *   orphans           — top-level узлы без входящих ссылок.
 *   broken-refs       — сломанные путь-ссылки в Сигналах.
 *   canvas-skeleton   — Канвас без VPC, VPC без Предложения, Предложение без
 *                       реализующей Услуги.
 *   unit-economics    — LTV/CAC<3, payback>12 мес, отрицательная маржа.
 *   summaries-coverage— сколько narrative-слотов stale/missing.
 *   coverage          — Сегменты без VPC, Работы без Предложения, Ситуации
 *                       без обслуживающего Канваса.
 *   vpc-redundancy    — несколько VPC в одном Канвасе с одинаковым
 *                       именем Предложения — кандидат на объединение.
 */
export type HealthAspectId =
    | 'health.unfounded'
    | 'health.disputed'
    | 'health.stale-signals'
    | 'health.empty-fields'
    | 'health.orphans'
    | 'health.broken-refs'
    | 'health.canvas-skeleton'
    | 'health.unit-economics'
    | 'health.summaries-coverage'
    | 'health.coverage'
    | 'health.vpc-redundancy'
    | 'health.confusable-names'
    | 'health.naming-style';

// ─────────────────────────────────────────────────────────────────────
// Атомы
// ─────────────────────────────────────────────────────────────────────

/**
 * Где «живёт» нарратив. Project — один на проект; Canvas — по одному на
 * каждый Канвас (адресуется именем).
 *
 * Юнит и Продукт как отдельные scope'ы пока не нужны: метрики Юнита
 * входят в `narr.project.intro`, а Продукт — в `narr.canvas.product`.
 * Если когда-нибудь захотим страницу-на-Юнит — добавится `unit`-вариант.
 */
export type AspectScope =
    | { kind: 'project' }
    | { kind: 'canvas'; canvas: string };

/**
 * Ссылка на сущность в snapshot'е. `type` — пользовательский тип DSL
 * («Сегмент», «Услуга»…), не AST-имя. `canvas` опционален и нужен только
 * для сущностей, у которых имя не уникально в проекте (например, Канал
 * существует только внутри Канваса).
 */
export interface EntityRef {
    type:   string;
    name:   string;
    canvas?: string;
}

/**
 * Полное представление сущности в snapshot'е: ref + текст блока DSL +
 * расположение в файле. Используется для entity-level diff между двумя
 * snapshot'ами: web рендерит side-by-side через CodeMirror MergeView.
 *
 * `source` — текст блока сущности из `$cstNode.text` (тот же текст,
 * что лежит в файле). Сохраняется как есть, без re-format'а: diff
 * между двумя версиями должен отражать ровно те правки, которые автор
 * сделал в файле.
 *
 * `file` — путь относительно корня проекта (subpath учтён).
 * `range` — координаты блока в `file` для клика «открыть в редакторе».
 */
export interface Entity {
    type:   string;
    name:   string;
    canvas?: string;
    source: string;
    file:   string;
    range:  TreeNodeLocation['range'];
}

/**
 * Значение поля, которое NarrativeBlock хочет показать рядом с
 * сущностью. Сложные структуры (списки, объекты) идут как
 * `NarrativeBlock.children`, не как `value`.
 */
export type ScalarValue = string | number | boolean;

/**
 * Поле сущности, которое блок хочет вывести — пара ключ/значение для
 * inline-вывода под узлом.
 */
export interface BlockField {
    key:   string;
    value: ScalarValue;
}

// ─────────────────────────────────────────────────────────────────────
// Цитаты — Сигналы / Развилки / Премиссы рядом с узлом
// ─────────────────────────────────────────────────────────────────────

/**
 * Цитата прикреплена к `NarrativeBlock` через reverse-index. Один и тот
 * же Сигнал может появиться в нескольких блоках (под Сегментом и под
 * Предложением, например) — это и есть смысл: показать причину рядом со
 * следствием. Дублирование осознано.
 *
 * `summary` — короткий текст для chip'а: для Сигнала это
 * `Категория · Дата`, для Развилки — `→ Выбрано`, для Премиссы —
 * `Тип · Пересмотр`. Полный текст разворачивается по клику и берётся
 * из самой сущности по `ref`.
 */
export type CitationKind = 'signal' | 'decision' | 'premise';

export interface Citation {
    kind:    CitationKind;
    ref:     EntityRef;
    summary: string;
    /**
     * Куда переходить при клике в IDE-дереве (декларация Сигнала/
     * Развилки/Премиссы). Ставит LSP-handler `pflow/narrativeView`
     * (см. `enrichBlocksWithLocations`); server-side endpoint и web
     * это поле не заполняют и не читают.
     */
    location?: TreeNodeLocation;
}

// ─────────────────────────────────────────────────────────────────────
// Health-флаги — inline-проекция HealthAspect.findings на узел
// ─────────────────────────────────────────────────────────────────────

/**
 * Health-флаг, прикреплённый к конкретному `NarrativeBlock`. Это
 * проекция глобального `HealthAspect.findings` — нужна, чтобы Web мог
 * нарисовать «⚠ Описание пусто» рядом с узлом без lookup'а в глобальный
 * health-список на каждом ререндере.
 *
 * Источник правды — `Snapshot.health`. Дубль здесь — кэш для рендера,
 * вычисляется при build'е снапшота.
 */
export interface HealthFlag {
    aspect:   HealthAspectId;
    message:  string;
    severity: Severity;
}

// ─────────────────────────────────────────────────────────────────────
// Структурный compute нарратива
// ─────────────────────────────────────────────────────────────────────

/**
 * Один узел структурного дерева нарратива. Может представлять сущность
 * (`entity` задан) или агрегатный заголовок (`entity` undefined, только
 * `label`). Рекурсивный через `children`.
 *
 * Пример (`narr.canvas.value` для B2C-Канваса стартера):
 *
 *   NarrativeBlock {                                 ← VPC «Премиум B2C»
 *     entity: { type: 'VPC', name: 'Премиум B2C' },
 *     fields: [
 *       { key: 'УТП', value: 'видишь весь процесс…' },
 *     ],
 *     children: [
 *       NarrativeBlock {                              ← список Альтернатив
 *         label: 'Альтернативы',
 *         children: [
 *           { entity: {type:'Альтернатива', name:'купить_новый_ковёр'} },
 *           { entity: {type:'Альтернатива', name:'чистить_самому_пеной'},
 *             citations: [{ kind:'signal', ref:…, summary:'…' }] },
 *           …
 *         ]
 *       },
 *       NarrativeBlock {                              ← вложенное Предложение
 *         entity: { type:'Предложение', name:'забор и возврат…' },
 *         fields: […],
 *         citations: [
 *           { kind:'signal', ref:…, summary:'метрика · 2026-Q1' },
 *           { kind:'decision', ref:…, summary:'→ видео_трансляция' },
 *         ],
 *         children: [
 *           NarrativeBlock { entity:{type:'Услуга', name:'…'} },  ← svcflow
 *         ],
 *         health: [
 *           { aspect:'health.unfounded', message:'…', severity:'attention' },
 *         ],
 *       },
 *     ],
 *   }
 *
 * Web рендерит этот узел и рекурсивно — children. `label` без `entity` —
 * это просто секция-заголовок («Альтернативы», «ERRC»).
 */
export interface NarrativeBlock {
    /** Сущность, которую блок описывает. Undefined для агрегатных секций. */
    entity?:    EntityRef;
    /** Заголовок секции, если `entity` нет; иначе берётся из `entity.name`. */
    label?:     string;
    /** Поля сущности для inline-вывода. */
    fields?:    BlockField[];
    /** Цитаты Сигналов/Развилок/Премисс, прикреплённые к этому узлу. */
    citations?: Citation[];
    /** Inline health-флаги (проекция глобального `Snapshot.health`). */
    health?:    HealthFlag[];
    /** Вложенные блоки. */
    children?:  NarrativeBlock[];
    /** Куда переходить при клике (location в DSL-файле). */
    location?:  TreeNodeLocation;
}

// ─────────────────────────────────────────────────────────────────────
// NarrativeAspect
// ─────────────────────────────────────────────────────────────────────

/**
 * Статус LLM-summary относительно текущего compute.
 *
 *   fresh   — `summary.inputHash` совпадает с `aspect.inputHash`.
 *   stale   — есть текст, но hash не совпадает (модель изменилась
 *             после генерации; нужен ре-ран агента).
 *   missing — записи в `summaries-v2.jsonl` нет вообще.
 */
export type NarrativeStatus = 'fresh' | 'stale' | 'missing';

/**
 * LLM-сгенерированный нарратив для одного `NarrativeAspect`.
 *
 *   - `text` — markdown, обычно 2–4 строки. Хранится в `.pflow/summaries-v2.jsonl`.
 *   - `inputHash` — hash контекста (`blocks` + соседние project-summaries),
 *     при котором генерилось. Если меняется — `status` становится stale.
 *   - `generatedBy` — метка источника: `agent:<name>` для агентских
 *     генераций, `human` если кто-то редактировал руками.
 */
export interface NarrativeSummary {
    text:        string;
    status:      NarrativeStatus;
    inputHash:   string;
    generatedAt: string;
    generatedBy: string;
}

/**
 * Один narrative-блок Canvas-view. Слот для LLM-нарратива + структурный
 * compute, который этим нарративом резюмируется.
 *
 *   - `summary === null` если запись в `summaries-v2.jsonl` отсутствует
 *     (status='missing'); Web рисует знак «?» вместо иконки свежести.
 *   - `inputHash` — hash от `blocks`. Меняется при изменении модели
 *     в зоне покрытия этого нарратива. На стейлинг summary смотрит
 *     `summary.inputHash === aspect.inputHash`.
 *   - `order` — позиция в scope (1..10 для project, 1..9 для canvas).
 *     Web рендерит в этом порядке.
 *   - `dataSources` — основные сущности нарратива (Юниты+Канвасы для
 *     intro, Премиссы для frames и т.д.). Whitelist для `aspect_save`
 *     и счётчик «эта сущность фигурирует в нарративах X, Y».
 *   - `secondaryDataSources` — citation-сущности (Сигналы/Развилки/
 *     Премиссы), которые цитируются хотя бы в одном `blocks[].citations`
 *     этого нарратива, минус то, что уже в `dataSources`. Агент может
 *     упомянуть их в нарративе («Сигнал X подтвердил Юнит»). Дерево
 *     IDE раскрывает их под соответствующим блоком, а не отдельной
 *     секцией — связь блок↔цитата сохраняется через `blocks[].citations`.
 */
export interface NarrativeAspect {
    id:                   NarrativeAspectId;
    scope:                AspectScope;
    title:                string;
    order:                number;
    summary:              NarrativeSummary | null;
    blocks:               NarrativeBlock[];
    inputHash:            string;
    dataSources:          EntityRef[];
    secondaryDataSources: EntityRef[];
}

// ─────────────────────────────────────────────────────────────────────
// HealthAspect
// ─────────────────────────────────────────────────────────────────────

/**
 * Одна находка health-чекера: что и где не так.
 *
 *   - `entity` — узел, на который ссылается находка. Используется и
 *     для inline-проекции в `NarrativeBlock.health`, и для клика
 *     «открыть в Canvas-view» из глобальной Problems-панели.
 *   - `anchor` — куда скроллить в Canvas-view. Может отсутствовать,
 *     если узел не показан ни в одном narrative-блоке (например,
 *     orphan-сущность); тогда клик откроет drawer/Справочник.
 */
export interface HealthFinding {
    entity:   EntityRef;
    message:  string;
    severity: Severity;
    anchor?:  { aspectId: NarrativeAspectId; scope: AspectScope };
    /**
     * Структурная подсказка «как починить» — заполняется чекером, который
     * знает точное лекарство. Прокидывается в `pflow_health_v2` и `check`,
     * чтобы агент видел не только «что не так», но и «куда добавить ссылку /
     * какое поле завести». Web/extension могут игнорировать.
     */
    fix?:     HealthFix;
    /**
     * Локация находки. Номера строк (1-based) выставляет чекер из CST:
     * `startLine`/`endLine` — диапазон всей сущности, `problemLine` —
     * конкретная проблемная строка (например, строка сломанной ссылки),
     * если она отличается от объявления. `file` (workspace-relative)
     * доставляет MCP-слой; shared builder его не ставит, чтобы не тащить
     * абсолютные пути в snapshot, отдаваемый web.
     */
    loc?:     { startLine: number; endLine: number; problemLine?: number; file?: string };
    /**
     * Навигационная локация (uri + range имени декларации). Ставит только
     * LSP-handler `pflow/narrativeView` для клика по находке в VS Code-дереве
     * «🩺 Здоровье». Web рисует переход через `anchor`, MCP — через `loc.file`;
     * оба это поле игнорируют, у server'а оно остаётся undefined.
     */
    location?: TreeNodeLocation;
}

/**
 * Подсказка к health-находке. `hint` — что сделать словами; `field` —
 * какое поле/в какой сущности это создаёт ссылку; `example` — готовый
 * фрагмент DSL; `see_tool` — какой MCP-инструмент помогает (pflow_refs,
 * pflow_fields, pflow_signals, …).
 */
export interface HealthFix {
    hint:      string;
    field?:    string;
    example?:  string;
    see_tool?: string;
}

/**
 * Нарратив-чекер модели. Каждый — отдельный детектор: один шейп, один
 * детектор. `severity` — максимум по `findings`; используется для
 * сортировки чекеров в Problems-панели.
 */
export interface HealthAspect {
    id:       HealthAspectId;
    title:    string;
    severity: Severity;
    findings: HealthFinding[];
    count:    number;
}

// ─────────────────────────────────────────────────────────────────────
// Glossary — боковая панель «Сущности»
// ─────────────────────────────────────────────────────────────────────

/**
 * Одна строка в Справочнике: сущность + куда ведёт клик + inline
 * health-флаги. `summary` — компактные 2–3 ключевых поля одной строкой
 * для chip'а («Размер 50 000 · Решает по доверию»).
 *
 * `anchor` указывает на конкретный narrative-блок Canvas-view, где
 * сущность показана в своём контексте (для Сегмента «Владелец…» это
 * `narr.canvas.audience` под B2C-Канвасом). Клик скроллит туда и
 * подсвечивает узел вспышкой.
 */
export interface GlossaryItem {
    entity:  EntityRef;
    summary: string;
    anchor:  { aspectId: NarrativeAspectId; scope: AspectScope };
    health?: HealthFinding[];
    /**
     * Сущность принадлежит рыночному Юниту (`Принадлежность: рынок`) —
     * research-модель конкурента, не наша декларация. Web-Справочник даёт
     * фильтр Наш/Рынок/Оба и раздельный счётчик по этому флагу.
     */
    market?: true;
    /**
     * Опциональная локация декларации сущности (uri + range). Заполняется
     * только в LSP-handler'е `pflow/narrativeView` для VS Code extension —
     * web рисует переход через `anchor` и uri ему не нужен.
     */
    location?: TreeNodeLocation;
}

/**
 * Группа Справочника по типу сущности. `dsl` — отдельный заголовок
 * pflow vs svcflow в UI, чтобы читатель видел границу двух DSL.
 */
export interface GlossaryGroup {
    type:  string;
    label: string;
    count: number;
    items: GlossaryItem[];
}

/**
 * Только те типы, которые scoped к Канвасу/Юниту и поэтому не имеют
 * project-wide narrative-блока со списком (Сегменты, Работы, VPC,
 * Предложения, Каналы, Ситуации, Переключения; вся svcflow-сторона).
 *
 * Project-wide типы (Премиссы, Развилки, Сигналы, Ресурсы, Партнёры,
 * Активности, Компоненты, Результаты, Связки, Петли) уже разворачиваются
 * внутри своего нарратива — в Справочнике их нет.
 */
export interface Glossary {
    pflow:   GlossaryGroup[];
    svcflow: GlossaryGroup[];
    dflow:   GlossaryGroup[];
}

// ─────────────────────────────────────────────────────────────────────
// CanvasView — координатор canvas-level нарративов
// ─────────────────────────────────────────────────────────────────────

/**
 * Страница одного Канваса. Соответствует одному экземпляру `pflow:Канвас`.
 *
 *   - `metrics` — для шапки Канваса: AOV, Клиентов/мес и т.п.
 *   - `aspects` — упорядоченный список canvas-level нарративов.
 *     `narr.canvas.product` может отсутствовать в списке, если у этого
 *     Канваса нет привязанного svcflow:Продукта (а не пустой слот).
 *
 * Порядок Канвасов в `Snapshot.canvases` определяется явным
 * `Юнит.Канвасы: [...]` (если декларирован) — это намерение автора.
 * Если Юнита нет — порядок их декларации в файлах.
 */
export interface CanvasView {
    name:    string;
    title:   string;
    metrics: AspectMetric[];
    aspects: NarrativeAspect[];
}

// ─────────────────────────────────────────────────────────────────────
// NarrativeView — общее ядро, которое возвращает buildNarrativeView
// ─────────────────────────────────────────────────────────────────────

/**
 * Результат `buildNarrativeView(roots)` — единое ядро narrative-сборки,
 * которое переиспользуют ВСЕ потребители v2:
 *   - server HTTP `/snapshot-v2` (оборачивает в `Snapshot`, добавляя
 *     `meta`/`diagnostics`/`entities`),
 *   - LSP `pflow/narrativeView` (отдаёт как есть, обогащая локациями),
 *   - MCP-tools (`pflow_aspect_v2_*`, `pflow_health_v2`).
 *
 * Контракт живёт здесь, в shared, чтобы и LSP-сервер, и extension-клиент
 * ссылались на ОДНО определение — transport-payload не дублируется.
 */
export interface NarrativeView {
    unit?:          { name: string; metrics: AspectMetric[] };
    projectAspects: NarrativeAspect[];
    canvases:       CanvasView[];
    glossary:       Glossary;
    health:         HealthAspect[];
    /** Дискавери-борд (dflow). Отсутствует, если планировочный слой пуст. */
    plan?:          PlanBoard;
    /** Лента Сигналов (новые сверху). Отсутствует, если Сигналов нет. */
    signals?:       SignalFeedEntry[];
}

/**
 * Запись ленты Сигналов — «что нового узнали»: хронологический пульс
 * дискавери для вкладки «Сигналы» сайдбара (стендап-поверхность).
 */
export interface SignalFeedEntry {
    name:         string;
    category?:    string;   // кабинет | эксперт | пользователь | продажа | метрика
    date?:        string;
    source?:      string;
    observation?: string;
    /** Приоритетный вектор: refutes > confirms > related; none — инбокс без цели. */
    vector:       'confirms' | 'refutes' | 'related' | 'none';
    /** Текст путь-ссылок целей (как в DSL). */
    targets:      string[];
}

// ─────────────────────────────────────────────────────────────────────
// PlanBoard — дискавери-борд (dflow) для страницы «План»
// ─────────────────────────────────────────────────────────────────────

/**
 * Структурный срез планировочного слоя для web-страницы `/r/:owner/:repo/plan`
 * и любых потребителей, которым нужен план как ДАННЫЕ, а не как рассказ
 * (рассказ — `narr.project.validation`). Все ссылки — именами (string):
 * дрилл-даун идёт через EntityCard по (титул, имя).
 */
export interface PlanBoard {
    plans:      PlanBoardPlan[];
    iterations: PlanBoardIteration[];
    hypotheses: PlanBoardHypothesis[];
    fits:       PlanBoardFit[];
    /** Каналы с Bullseye-статусом (только наши — рыночные не участвуют). */
    rings:      PlanBoardRing[];
}

export interface PlanBoardPlan {
    name:       string;
    status?:    string;   // активный | закрытый
    goals:      string[]; // → pflow:Результат
    hypotheses: string[];
    iterations: string[];
    outcome?:   string;   // Итог квартального разбора
}

export interface PlanBoardIteration {
    name:      string;
    status?:   string;   // планируется | идёт | закрыта
    size?:     string;   // неделя | две_недели
    goal?:     string;
    capacity?: number;
    focus:     string[]; // имена Гипотез в спринте
    outcome?:  string;
}

export interface PlanBoardHypothesis {
    name:          string;
    statement?:    string;
    status?:       string;   // бэклог | в_проверке | подтверждена | опровергнута | отложена
    horizon?:      string;   // сейчас | дальше | потом
    risk?:         string;   // ценность | удобство | реализуемость | жизнеспособность
    confidence?:   string;
    method?:       string;
    /** Порог «да», зафиксированный до эксперимента. */
    confirmSignal?: string;
    /** Порог «нет» — после которого гипотезу меняем, а не докручиваем. */
    rejectSignal?: string;
    /** Legacy-порог `Ожидаем:` (если раздельные не заданы). */
    expect?:       string;
    verdict?:      string;
    checks:        string[]; // → pflow:Предложение
    moves:         string[]; // → pflow:Показатель
    channels:      string[]; // → pflow:Канал (channel-гипотезы)
    segments:      string[];
    /** Итерация, в чей Фокус входит гипотеза (reverse). */
    iteration?:    string;
}

export interface PlanBoardFit {
    name:      string;
    kind?:     string;   // продукт_рынок | продукт_канал | канал_модель | модель_рынок
    status?:   string;   // гипотеза | достигнут | под_угрозой | сломан
    between:   string[];
    checkedBy: string[];
}

export interface PlanBoardRing {
    channel: string;
    ring:    string;    // кандидат | тестируем | победитель | отброшен
    canvas?: string;
}

// ─────────────────────────────────────────────────────────────────────
// Snapshot v2 — top-level контракт страницы проекта
// ─────────────────────────────────────────────────────────────────────

/**
 * `Snapshot` v2 — итоговая модель, которую builder возвращает Web/MCP.
 *
 *   - `unit` — Юнит и его портфельные метрики. Может отсутствовать в
 *     минимальных проектах без декларированного Юнита; тогда шапка
 *     страницы рисуется по `meta` (owner/repo/sha) без портфеля.
 *   - `projectAspects` — фиксированный набор project-нарративов в порядке `order`.
 *     Если у проекта нет соответствующих сущностей (например, ни одного
 *     Партнёра) — нарратив всё равно присутствует, но `blocks` пустые;
 *     summary показывает «нет данных» вместо missing-状況 (это разные
 *     состояния: missing — забыли сгенерировать; пусто — нечего сказать).
 *   - `canvases` — N штук в порядке `Юнит.Канвасы` или декларации.
 *   - `glossary` — производная структура для боковой панели «Сущности».
 *   - `health` — health-чекеры; могут иметь `findings === []`,
 *     это нормальное состояние «всё хорошо по этому чекеру».
 *   - `diagnostics` — парсер-ошибки (errors/warnings), отдельно от health
 *     (это про синтаксис, не про продуктовую модель).
 */
export interface Snapshot extends NarrativeView {
    meta:           SnapshotMeta;
    diagnostics:    SnapshotDiagnostics;
    /**
     * Полный набор top-level (включая canvas-scoped) сущностей проекта с
     * сохранённым DSL-текстом и расположением в файле. Источник правды
     * для entity-level diff (см. `@pflow/shared/v2/diff`).
     *
     * Сортировка стабильна (type → canvas → name), чтобы diff между
     * двумя snapshot'ами не зависел от порядка обхода Langium.
     */
    entities:       Entity[];
}

// ─────────────────────────────────────────────────────────────────────
// ChatPageContext — какая страница веб-приложения открыта у пользователя
// ─────────────────────────────────────────────────────────────────────

/**
 * Контекст текущей страницы, который web шлёт серверу в `/chat`. Сервер
 * инжектит компактное описание в system prompt и регистрирует подходящие
 * tool'ы (diff-tool'ы — только при `view: 'compare'`).
 *
 * Контракт курсирует между web (producer) и server (consumer); живёт в
 * shared, чтобы обе стороны типизировались от одной декларации.
 *
 *   - canvas: пользователь смотрит Canvas-view. `canvas`/`aspectId` —
 *     якорь, если в URL'е `#aspect-<scope>-<id>`. `openEntity` — EntityCard
 *     drawer, открытый через клик по сущности.
 *   - compare: страница `/compare`, две точки одного репо.
 *   - print: print-view; чат не показываем, но на всякий случай.
 *   - unknown: всё остальное / fallback. Сервер ведёт себя как раньше.
 */
export type ChatPageContext =
    | {
        view:        'canvas';
        canvas?:     string;
        aspectId?:   string;
        openEntity?: EntityRef;
    }
    | {
        view:    'compare';
        baseSha: string;
        headSha: string;
        baseRef?: string;
        headRef?: string;
        subpath?: string;
    }
    | {
        view:        'plan';
        openEntity?: EntityRef;
    }
    | {
        view:        'reports';
        openEntity?: EntityRef;
    }
    | { view: 'print' }
    | { view: 'unknown' };

// ─────────────────────────────────────────────────────────────────────
// Каталог: метаданные для UI/builder/MCP
// ─────────────────────────────────────────────────────────────────────

/**
 * Метаданные одного `NarrativeAspectId` — порядок, человеческий
 * заголовок, источник данных. Единственная мап-таблица, по которой
 * builder знает, в каком порядке рендерить нарративы, а Web — что
 * писать в title.
 *
 * Размещается здесь, в shared, чтобы и server-builder, и web-UI, и MCP
 * читали один и тот же реестр. Параллельных таблиц быть не должно.
 */
export interface NarrativeAspectMeta {
    id:    NarrativeAspectId;
    /** В каком scope живёт нарратив. */
    scope: 'project' | 'canvas';
    /** Порядок внутри scope. */
    order: number;
    /** Заголовок для UI. */
    title: string;
    /** Короткое описание для tooltip'а / docs. */
    description: string;
    /** Эмодзи для шапки блока. */
    emoji?: string;
    /**
     * Инструкция для агента: что должно быть в summary этого нарратива,
     * в какой структуре, в каком тоне, какой длины. Markdown с явными
     * секциями `**Что:**`, `**Структура:**`, `**Тон:**`, `**Длина:**`.
     *
     * Возвращается MCP-tool'ом `pflow_aspect_v2_context` в поле `prompt`.
     * Агент использует как контракт того, как должен выглядеть «зелёный»
     * summary именно для этого нарратива (intro — описательный, hypothesis
     * — уверенный, open-questions — чек-лист и т.п.).
     *
     * Дополнительно: проектная `voice.md` (если есть) задаёт общий тон
     * поверх. Промпт здесь — про **семантику слота**, voice.md — про
     * **голос проекта**. Не путать.
     */
    agentPrompt: string;
}

/**
 * Project-нарративы, которые показываются **до** Канвасов (фрейм чтения).
 * Остальные project-нарративы — Поддержка/Backstage (показываются после).
 * Единственный источник правды для web (страница, печать, оглавление) и
 * extension (дерево сущностей) — параллельных списков не заводить.
 *
 * Состав фрейма — это след решения целиком: чем ограничены (frames) →
 * что решили и почему (decisions) → что ещё не решили (open-questions).
 * `open-questions` живёт здесь, а не в бэкстейдже, потому что это прямое
 * продолжение решений: разрывать пару «решённое / нерешённое» Канвасами
 * значит заставлять читателя скроллить туда-обратно.
 *
 * `evidence` и `validation` сознательно остаются в бэкстейдже: это
 * справочный материал, в который заходят по значку 📡 с конкретного узла,
 * а не читают линейно. Лента из сотни Сигналов и полный план валидации
 * перед Канвасами утопили бы ответ на вопрос «что вы вообще продаёте».
 */
export const FRAME_IDS: ReadonlySet<ProjectNarrativeAspectId> = new Set<ProjectNarrativeAspectId>([
    'narr.project.intro',
    'narr.project.market',
    'narr.project.frames',
    'narr.project.decisions',
    'narr.project.open-questions',
]);

export const NARRATIVE_ASPECTS: readonly NarrativeAspectMeta[] = [
    // ── Project: FRAME (показывается до Канвасов) ──────────────────
    {
        id: 'narr.project.intro',      scope: 'project', order: 1,
        emoji: '📦',
        title: 'О проекте',
        description: 'Что за проект: Юнит, ключевая ставка, логика портфеля.',
        agentPrompt:
            '**Что:** проект в обзоре — что продаёт, кому, ключевая ставка (что предполагаем о рынке и чем закрываем), портфельные метрики. ' +
            'Если Канвасов несколько — как они усиливают друг друга (Связки/Петли: «X→Y: триггер Z»), без перечисления «Канвас A, Канвас B». ' +
            '**Структура:** 1-2 параграфа: первый — что за бизнес и ставка, второй (если есть) — логика портфеля. ' +
            '**Тон:** третьим лицом, дескриптивный, без маркетингового пафоса. Не используй «успешный/инновационный/уникальный». ' +
            '**Длина:** 3-6 предложений.',
    },
    {
        id: 'narr.project.market',     scope: 'project', order: 2,
        emoji: '🌍',
        title: 'Что делает рынок',
        description: 'Разобранные конкуренты (рыночные Юниты), мосты из Альтернатив, сигналы по рынку.',
        agentPrompt:
            '**Что:** конкурентная карта. Разобранные конкуренты (Юниты с Принадлежность: рынок): чем берут, где у них пусто «снаружи» — и что из этого следует для нас (пустота у конкурента = возможная ниша или «не работает»). ' +
            'Альтернативы с мостом Модель — как клиент решает задачу без нас. Сигналы по конкурентам — с датой и источником. ' +
            '**Структура:** 1 параграф на конкурента/группу альтернатив, формат «X берёт Y; снаружи не видно Z — наша ставка W». ' +
            '**Тон:** разведывательный, только наблюдаемое снаружи. Не приписывай конкурентам намерений. ' +
            '**Длина:** 2-4 предложения.',
    },
    {
        id: 'narr.project.frames',     scope: 'project', order: 3,
        emoji: '⚖️',
        title: 'Какие у нас рамки',
        description: 'Предпосылки — scope-ограничения и founder\'s bets.',
        agentPrompt:
            '**Что:** решения, принятые до рынка — рамки, которые мы не проверяем экспериментом. Группировка: твёрдые → на_срок → мягкие. ' +
            'Для каждой — что, почему (Причина) и кто задал (Источник); для на_срок — дата пересмотра. ' +
            'Разница с Развилками: там мы выбирали из вариантов, здесь — очертили поле, внутри которого вообще идёт выбор. ' +
            '**Структура:** 1 параграф, фразы вида «твёрдо: X, Y; на срок до Z: W». ' +
            '**Тон:** деловой, без оправданий. Не объясняй почему «отказались» — просто констатируй scope. ' +
            '**Длина:** 3-4 предложения.',
    },
    {
        id: 'narr.project.decisions',  scope: 'project', order: 4,
        emoji: '🔀',
        title: 'Что решили и почему',
        description: 'Принятые решения с историей: что выбрали, что отвергли, при каком условии вернёмся.',
        agentPrompt:
            '**Что:** принятые решения и путь к ним. По каждой решённой Развилке обязательно: что выбрали, ' +
            '**что при этом отвергли** (Варианты минус Выбрано) и почему, **при каком условии вернёмся** к вопросу (Пересмотреть_если). ' +
            'Отвергнутая ветка и триггер пересмотра — не факультатив: без них получается «мы решили X», а не «мы прошли развилку». ' +
            'Если решение опирается на Сигналы (они бьют путь-ссылкой в Развилку) — назови их как основание. В конце — что ещё решаем. ' +
            '**Структура:** обратная хронология, свежее первым; 1 предложение на развилку. ' +
            'Маркеры: ✅ решено, ♻️ пересмотр триггернут, 🟡 ещё решаем. ' +
            'Формат «✅ X (дата): выбрали Y вместо Z, потому что W; вернёмся, если V». ' +
            '**Тон:** дескриптивный, без хвастовства про «правильный выбор». Просто констатация. ' +
            '**Длина:** 3-6 предложений (1 на каждую развилку).',
    },

    {
        id: 'narr.project.open-questions', scope: 'project', order: 5,
        emoji: '❓',
        title: 'Что ещё не решено',
        description: 'Открытые развилки, Ситуация.Неясно, Премиссы на_срок, fit\'ы под угрозой — чек-лист.',
        agentPrompt:
            '**Что:** чек-лист действий и открытых вопросов. Берёшь из открытых Развилок, Ситуация.Неясно, Премисс с Пересмотр: на_срок, Соответствий со Статус: под_угрозой/сломан. ' +
            'Это прямое продолжение «Что решили и почему»: там — закрытые развилки, здесь — ещё не закрытые. ' +
            '**Структура:** 3-5 пунктов, каждый — конкретный вопрос или задача. Без введения «нам нужно проверить» — сразу пункты. ' +
            'Можно использовать markdown-список «- ...». ' +
            '**Тон:** повелительный, action-oriented. Без «возможно стоит» — «сделать», «проверить», «спросить». ' +
            '**Длина:** 3-5 пунктов, по одной строке каждый.',
    },

    // ── Project: BACKSTAGE (показывается после Канвасов) ───────────
    {
        id: 'narr.project.evidence',   scope: 'project', order: 6,
        emoji: '📡',
        title: 'Откуда мы знаем',
        description: 'Сигналы — наблюдения, метрики, интервью.',
        agentPrompt:
            '**Что:** сводка доказательной базы. Не перечисляй все Сигналы — назови сильнейшие подтверждения и сильнейшие опровержения. ' +
            'Вес по категории: метрика > продажа > пользователь/эксперт > кабинет (кабинетный сигнал — из открытых источников, самый слабый). Указывай категорию и дату. ' +
            '**Структура:** 2-3 предложения, формат «Подтверждено X [метрика, 2026]; опровергнуто Y [интервью, 2025-11]; не подтверждено пока Z». ' +
            '**Тон:** dispassionate, экспертный. Не «хорошие сигналы» — конкретно. ' +
            '**Длина:** 2-3 предложения.',
    },
    {
        id: 'narr.project.validation', scope: 'project', order: 7,
        emoji: '🧪',
        title: 'Что проверяем и куда двигаем',
        description: 'Метрики-ставки (Результаты/Показатели) + dflow-план + Соответствия (Four Fits).',
        agentPrompt:
            '**Что:** валидационный контур проекта. Метрики-ставки: клиентские Результаты (ODI) и наши Показатели с целями-цифрами. ' +
            'План проверки: активный квартальный План, фокус спринта, ключевые Гипотезы (что проверяют, какой Показатель двигают, пороги Сигнал_подтверждения/Сигнал_отказа). ' +
            'Соответствия (Four Fits): какие пары осей достигнуты, какие в гипотезе или под угрозой; Кольца каналов (кто победитель Bullseye, кто в тесте). ' +
            '**Структура:** 2 абзаца: первый — метрики-ставки, второй — «квартал X ставит на Y; в фокусе — гипотеза Z (порог W); fit продукт_рынок достигнут, продукт_канал решается гипотезой V». ' +
            '**Тон:** операционный, количественный. Без «мы верим» — констатация: что проверяется, чем, против какого порога. ' +
            '**Длина:** 4-7 предложений.',
    },
    // ── Canvas (порядок = порядок чтения Концепта проекта) ─────────
    {
        id: 'narr.canvas.model',      scope: 'canvas', order: 1,
        emoji: '🧩',
        title: 'Канвас бизнес-модели',
        description: '9 блоков BMC одной страницей + логика связности модели.',
        agentPrompt:
            '**Что:** бизнес-модель Канваса по 9 блокам BMC, но не анкетой, а связным рассказом: кому (сегменты кратко — детали в «Профиле клиента»), какую ценность (УТП/Преимущество), через какие каналы находят и получают (+статус Кольца Bullseye), какой тип отношений (разовые/подписка), на чём зарабатываем, на каких ресурсах/активностях/партнёрах держимся, куда уходят деньги. ' +
            'Финал — логика модели: где цепочка «сегмент → ценность → ресурсы → затраты» сходится, а где есть разрыв (дорогой сегмент + дешёвая модель доходов и т.п.). ' +
            '**Структура:** 2-3 абзаца: (1) кому/что/как доставляем, (2) на чём держимся и экономика, (3) логика связности — 1-2 предложения. ' +
            '**Тон:** дескриптивный, без «комплексный подход/индивидуальные решения» — проверка: если фраза верна и для конкурента, она не работает. ' +
            '**Длина:** 5-8 предложений.',
    },
    {
        id: 'narr.canvas.audience',   scope: 'canvas', order: 2,
        emoji: '👤',
        title: 'Профиль клиента',
        description: 'Сегменты (+Не_наш, ЛПР) + Работы JTBD + Боли/Выгоды профиля VPC.',
        agentPrompt:
            '**Что:** профиль клиента целиком. Сегмент(ы) человеческим языком: кто это, размер ниши, ранние, кто принимает решение (ЛПР) — и кого осознанно НЕ берём (Не_наш). ' +
            'Его Работа: когда возникает (триггер), что хочет, чтобы что, главное «но»-барьер. ' +
            'Боли и Выгоды профиля — с привязкой к ступени лестницы, где они доминируют (смещение боли толкает вверх). ' +
            '**Структура:** 2-3 абзаца: сегмент → работа → боли/выгоды по ступеням. ' +
            '**Тон:** human-language, не маркетинговый. Не «целевая аудитория» — «собственник 2-20 точек, у которого...». Без терминов ICP/TAM/JTBD. ' +
            '**Длина:** 4-7 предложений.',
    },
    {
        id: 'narr.canvas.ladder',     scope: 'canvas', order: 3,
        emoji: '🪜',
        title: 'Продуктовая лестница',
        description: 'VPC-карты ценности по ступеням: Тип_выгоды, Снимает/Создаёт, связность лестницы, Альтернативы, ERRC.',
        agentPrompt:
            '**Что:** ценностное предложение как лестница. По каждой ступени (Предложению): тип выгоды (ясность/решение/удержание), какие Боли снимает и Выгоды создаёт, статус. ' +
            'Проверка связности: у каждой ступени своя доминирующая боль; каждая самостоятельна; каждая следующая опирается на предыдущую. ' +
            'Контекст: что рынок делает вместо нас (Альтернативы), чем отстраиваемся (УТП, ERRC — 1-2 сильных пункта). ' +
            '**Структура:** абзац на ступень в порядке лестницы + финальный абзац связности. ' +
            '**Тон:** уверенный, evidence-based. Цитируй подтверждающие Сигналы по имени и дате. Не завышай тип выгоды: трипвайер продаёт «увидел», не «починил». ' +
            '**Длина:** 4-7 предложений.',
    },
    {
        id: 'narr.canvas.product',    scope: 'canvas', order: 4,
        emoji: '📦',
        title: 'Как это упаковано',
        description: 'svcflow:Продукт — Уровни, Опции, Трек, Апгрейды, границы Не_входит.',
        agentPrompt:
            '**Что:** упаковка лестницы операционно. Основная Услуга, уровни, опции (add-on), Трек прохождения сегмента, Апгрейды между ступенями (условие + конверсия). ' +
            'Границы: что НЕ входит (Не_входит) — где заканчивается ответственность, чтобы клиент не ждал бесконечной поддержки бесплатно. ' +
            '**Структура:** 1-2 абзаца. ' +
            '**Тон:** продуктовый, операционный. ' +
            '**Длина:** 3-5 предложений.',
    },
    {
        id: 'narr.canvas.case',       scope: 'canvas', order: 5,
        emoji: '🏆',
        title: 'Демо-кейс',
        description: 'Кейсы — реальный клиент через лестницу, этапы с результатами.',
        agentPrompt:
            '**Что:** демо-кейс(ы) — реальный клиент, прошедший по лестнице. Кто клиент (ниша, стартовая ситуация), по этапам: с какой боли начался, что сделали, что получил, измеримый результат. ' +
            'Финал — вывод: почему лестница сработала (аргумент для следующих продаж). Статус публикации важен: опубликованный кейс — маркетинговый актив. ' +
            '**Структура:** абзац на кейс: клиент → этапы одной нитью → результат цифрами → вывод. ' +
            '**Тон:** повествовательный, но с цифрами. Не «клиент доволен» — «наценка выросла с X до Y». ' +
            '**Длина:** 3-6 предложений на кейс.',
    },
    {
        id: 'narr.canvas.blueprint',  scope: 'canvas', order: 6,
        emoji: '🗺️',
        title: 'Как услуга устроена по шагам',
        description: 'Service Blueprint: Шаги × слои Клиент/Frontstage/Backstage/Стык.',
        agentPrompt:
            '**Что:** услуга во времени. Шаги слева направо: что делает клиент, что он видит (Frontstage), что скрыто (Backstage), где точки передачи (Стык). ' +
            'Линия видимости: клиент судит только по тому, что над ней — назови шаги с Видимость_критична и почему там молчание опасно. ' +
            '**Структура:** 1-2 абзаца: нить процесса + отдельно критичные по видимости шаги. ' +
            '**Тон:** операционный, конкретный. ' +
            '**Длина:** 3-6 предложений.',
    },
    {
        id: 'narr.canvas.risks',      scope: 'canvas', order: 7,
        emoji: '🛡️',
        title: 'Где рвётся и где решается доверие',
        description: 'Риски (FMEA, RPN), Моменты истины, красные зоны, контрмеры.',
        agentPrompt:
            '**Что:** карта рисков процесса. Топ-риски по Тяжести (RPN = Вероятность×Тяжесть×Обнаружимость — вторичный ключ; тяжесть последствий важнее произведения шкал): на каком шаге, видит ли клиент (и как интерпретирует), контрмера. ' +
            'Моменты истины: где клиент решает «этим можно доверять». Красные зоны: шаги, где видимый риск совпадает с моментом истины — приоритет №1. ' +
            '**Структура:** 1-2 абзаца: топ-риски с контрмерами → моменты истины → красная зона отдельным предложением. ' +
            '**Тон:** инженерный, без драматизации. Риск + число + контрмера. ' +
            '**Длина:** 3-6 предложений.',
    },
    {
        id: 'narr.canvas.triggers',   scope: 'canvas', order: 8,
        emoji: '🎯',
        title: 'Триггеры и вход',
        description: 'Ситуации (CEP) + Переключения (4 силы) — из чего складывается сообщение.',
        agentPrompt:
            '**Что:** как клиент узнаёт свою ситуацию и приходит. Ситуации (CEP): триггер-момент, ЛПР, срочность — выдели критическую. ' +
            'Путь переключения по 4 силам: что было плохо (толчок), что обещаем (притяжение), чего боится (тревога), от чего отказывается (привычка). ' +
            'Финал — из чего складывается сообщение: узнавание ситуации + момент действия + предложение. ' +
            '**Структура:** 2 абзаца: ситуации → переключение и сообщение. ' +
            '**Тон:** повествовательный, от лица клиента, без жаргона «CEP/Switch». ' +
            '**Длина:** 4-6 предложений.',
    },
    {
        id: 'narr.canvas.economics',  scope: 'canvas', order: 9,
        emoji: '💰',
        title: 'Фин модель',
        description: 'Доходы, Расходы, unit-economics каналов (CAC/LTV/payback), P&L.',
        agentPrompt:
            '**Что:** фин модель Канваса. Модель монетизации (разово/подписка/почасовая/процент_результата) с целью-цифрой, фиксированные Расходы, выводимая маржа. ' +
            'Unit-economics каналов: CAC/LTV/конверсия/payback по каждому; вывод — здоровая экономика или на грани, какой канал кормит модель. ' +
            '**Структура:** 1-2 абзаца: монетизация и P&L → каналы с цифрами и вывод. ' +
            '**Тон:** финансовый, точные цифры. ' +
            '**Длина:** 3-5 предложений.',
    },
];

/**
 * Глобальный гайд по разметке нарратива — добавляется ко всем
 * `agentPrompt` при возврате через `pflow_aspect_v2_context`. Описывает
 * **как** размечать текст внутри `параграф` / `буллет` / `длина`,
 * которые задаёт per-aspect prompt. Контракт остаётся: если slot
 * требует «1 параграф без буллетов» — соблюдай, но используй переносы
 * и **bold** внутри параграфа.
 *
 * Применяется поверх per-aspect prompt'а и **до** проектного voice.md.
 * Voice.md может переопределить или дополнить (например, запретить
 * эмодзи). Источник правды — этот файл.
 */
/**
 * Методологический праймер — добавляется ко всем `agentPrompt` при возврате
 * через `pflow_aspect_v2_context`, ПЕРЕД markup-гайдом. Пишущий summary агент
 * может не знать семантики pflow — праймер даёт минимум, который нельзя
 * нарушать ни в одном слоте. Per-aspect prompt уточняет, праймер — базовый
 * слой. Держать компактным: он входит в каждый context-ответ.
 */
export const NARRATIVE_METHODOLOGY_PRIMER: string =
    '**Методология (общее для всех слотов):** ' +
    'Факты — только из blocks/data_sources; сущности вне whitelist не упоминай, числа и даты не выдумывай. ' +
    'Эпистемика: декларация без Сигнала — гипотеза (говори «предполагаем/ставим», не «знаем»); ' +
    'узел под твёрдой Премиссой — осознанная ставка (констатируй без оправданий); ' +
    'подтверждено — только то, на что есть Сигнал (ссылайся с датой). ' +
    '«Результат» — метрика КЛИЕНТА, «Показатель» — НАША метрика; не смешивай. ' +
    'Рыночные сущности (Принадлежность: рынок) — наблюдения о конкурентах, не наши декларации: «у X видно Y», не «мы». ' +
    'Кейс — маркетинговое доказательство, Сигналы — evidence; цифры кейса не выдавай за проверенные метрики без Сигнала. ' +
    'Модель date-free: горизонты и статусы вместо сроков; календарные обещания не сочиняй.';

export const NARRATIVE_MARKUP_GUIDANCE: string =
    '**Разметка (общее правило):** ' +
    'разноси смысловые блоки пустой строкой (`\\n\\n`); ' +
    'выделяй ключевые сущности, метрики и числа через `**bold**` ' +
    '(имена Сегментов, суммы, проценты, даты, статусы); ' +
    'оборачивай в `_italic_` скобочные пояснения роли/типа («_(твёрдая)_», «_(социальная)_», «_(главный поток ценности)_»); ' +
    'ставь эмодзи-маркеры в начало смысловых блоков по семантике — ✅/🟡 для решённых/открытых развилок, ' +
    '🔒/⚠️ для твёрдых/мягких предпосылок, 📈/⏱️/🔁 для метрик-ставок, 💼/🔁/💰 для разовой/подписки/расходов, ' +
    '🆓/💸/🤝 для бесплатного/платного/партнёрского каналов, 🎯/🌱 для главного сегмента/второй волны, ' +
    '📌/🎁/⚠️/📩 для толчка/притяжения/тревоги/решения в Switch; ' +
    'плейсхолдеры (`0`, `TODO`) — в `backticks`. ' +
    'Это поверх per-aspect-контракта: если slot требует «1 параграф без буллетов» — буллеты не вводи, ' +
    'но переносы и bold внутри параграфа — да. Если slot разрешает буллеты — `- ` и пустая строка между пунктами.';

/**
 * Метаданные одного `HealthAspectId`. Минимальная информация для UI —
 * заголовок и эмодзи; severity и findings приходят с конкретными
 * данными при build'е.
 */
export interface HealthAspectMeta {
    id:    HealthAspectId;
    title: string;
    description: string;
    emoji?: string;
}

export const HEALTH_ASPECTS: readonly HealthAspectMeta[] = [
    {
        id: 'health.unfounded', emoji: '🌫️',
        title: 'Декларации без опоры',
        description: 'Узлы без Сигнала и без твёрдой Премиссы.',
    },
    {
        id: 'health.disputed', emoji: '⚔️',
        title: 'Противоречия в модели',
        description: 'Конфликтующие декларации между связанными узлами.',
    },
    {
        id: 'health.stale-signals', emoji: '🕰️',
        title: 'Старые Сигналы',
        description: 'Сигналы давностью больше порога свежести.',
    },
    {
        id: 'health.empty-fields', emoji: '🕳️',
        title: 'Пустые описания',
        description: 'Описание/УТП/Преимущество, без которых Canvas-view деградирует.',
    },
    {
        id: 'health.orphans', emoji: '👻',
        title: 'Сущности без входящих ссылок',
        description: 'Top-level узлы, на которые никто не ссылается.',
    },
    {
        id: 'health.broken-refs', emoji: '💔',
        title: 'Сломанные путь-ссылки',
        description: 'Сигналы с целями, которые не резолвятся.',
    },
    {
        id: 'health.canvas-skeleton', emoji: '🦴',
        title: 'Неполная структура',
        description: 'Канвас без VPC, VPC без Предложения, Предложение без Услуги.',
    },
    {
        id: 'health.unit-economics', emoji: '📉',
        title: 'Юнит-экономика под порогом',
        description: 'LTV/CAC<3, payback>12 мес, отрицательная маржа.',
    },
    {
        id: 'health.summaries-coverage', emoji: '✨',
        title: 'Покрытие нарративом',
        description: 'Сколько narrative-слотов stale или missing.',
    },
    {
        id: 'health.coverage', emoji: '🗺️',
        title: 'Покрытие модели',
        description: 'Сегменты без VPC, Работы без Предложения, Ситуации без Канваса.',
    },
    {
        id: 'health.vpc-redundancy', emoji: '🧬',
        title: 'Избыточные VPC',
        description: 'Несколько VPC в одном Канвасе с одинаковым Предложением — кандидат на слияние.',
    },
    {
        id: 'health.confusable-names', emoji: '🔤',
        title: 'Путаемые имена',
        description: 'Имена сущностей, различающиеся только регистром или разделителями (_ - пробел) — легко спутать, ломают подстрочный поиск.',
    },
    {
        id: 'health.naming-style', emoji: '🏷️',
        title: 'Имена не по-человечески',
        description: 'Имена в code-style (подчёркивания) или со строчной буквы — с pflow работают не программисты, имя должно быть человекочитаемой фразой с заглавной.',
    },
];

// ─────────────────────────────────────────────────────────────────────
// Type guards и утилиты
// ─────────────────────────────────────────────────────────────────────

export function scopeKey(scope: AspectScope): string {
    return scope.kind === 'project' ? 'project' : `canvas:${scope.canvas}`;
}

/** Стабильный ключ для строки `summaries-v2.jsonl`. */
export function summaryKey(id: NarrativeAspectId, scope: AspectScope): string {
    return `${id}#${scopeKey(scope)}`;
}
